/**
 * BindleTermResolveDialog
 * 
 * Shown before saving a bindle when loose search terms match inventory items.
 * Lets the user choose to convert each loose term to an exact match (quoted)
 * or keep it as a loose search.
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ResolvedTerm } from '../../utils/resolveBindleTerms';
import ModalCloseButton from '../ModalCloseButton';

interface BindleTermResolveDialogProps {
  /** Loose terms that have inventory matches */
  looseTerms: ResolvedTerm[];
  /** Called with a Map of original term → chosen exact name (or null to keep loose) */
  onConfirm: (conversions: Map<string, string | null>) => void;
  /** Called when user cancels */
  onCancel: () => void;
}

const BindleTermResolveDialog: React.FC<BindleTermResolveDialogProps> = ({
  looseTerms,
  onConfirm,
  onCancel,
}) => {
  // For each loose term, track the user's choice: an exact name or null (keep loose)
  const [choices, setChoices] = useState<Map<string, string | null>>(() => {
    const initial = new Map<string, string | null>();
    for (const term of looseTerms) {
      // Default: select the first match (most likely correct)
      initial.set(term.original, term.matchedNames[0]);
    }
    return initial;
  });

  const handleChoice = (termOriginal: string, value: string | null) => {
    setChoices(prev => {
      const next = new Map(prev);
      next.set(termOriginal, value);
      return next;
    });
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200001,
        padding: '0',
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: 'var(--color-bg-primary)',
          borderRadius: '12px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '16px 16px 12px',
          borderBottom: '1px solid var(--color-border)',
          position: 'relative',
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, paddingRight: '32px' }}>
            Match Search Terms to Items
          </h3>
          <ModalCloseButton
            onClick={onCancel}
            style={{ position: 'absolute', right: 12, top: 10 }}
          />
          <p style={{
            margin: '8px 0 0',
            fontSize: '13px',
            color: 'var(--color-text-muted)',
            lineHeight: 1.4,
          }}>
            These search terms match items in your inventory. Convert to exact matches so items can be tracked and re-added accurately.
          </p>
        </div>

        {/* Term list */}
        <div style={{ padding: '12px 16px' }}>
          {looseTerms.map((term) => (
            <div
              key={term.original}
              style={{
                marginBottom: looseTerms.indexOf(term) < looseTerms.length - 1 ? '16px' : 0,
              }}
            >
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: '6px',
              }}>
                "{term.original}" matches:
              </div>

              {/* Radio options for each matched name */}
              {term.matchedNames.map((name) => (
                <label
                  key={name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    background: choices.get(term.original) === name
                      ? 'var(--color-accent-secondary-light, rgba(59,130,246,0.08))'
                      : 'transparent',
                    marginBottom: '2px',
                    fontSize: '14px',
                  }}
                >
                  <input
                    type="radio"
                    name={`term-${term.original}`}
                    checked={choices.get(term.original) === name}
                    onChange={() => handleChoice(term.original, name)}
                    style={{ accentColor: 'var(--color-accent-secondary)' }}
                  />
                  <span style={{ fontWeight: 500 }}>"{name}"</span>
                  <span style={{ color: 'var(--color-text-faint)', fontSize: '12px' }}>(exact)</span>
                </label>
              ))}

              {/* Option to keep loose */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 10px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  background: choices.get(term.original) === null
                    ? 'var(--color-accent-secondary-light, rgba(59,130,246,0.08))'
                    : 'transparent',
                  fontSize: '14px',
                }}
              >
                <input
                  type="radio"
                  name={`term-${term.original}`}
                  checked={choices.get(term.original) === null}
                  onChange={() => handleChoice(term.original, null)}
                  style={{ accentColor: 'var(--color-accent-secondary)' }}
                />
                <span>Keep as "{term.original}"</span>
                <span style={{ color: 'var(--color-text-faint)', fontSize: '12px' }}>(loose)</span>
              </label>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{
          padding: '12px 16px 16px',
          borderTop: '1px solid var(--color-border)',
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              border: '1px solid var(--color-border-strong)',
              borderRadius: '6px',
              background: 'var(--color-bg-primary)',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(choices)}
            style={{
              padding: '8px 20px',
              border: 'none',
              borderRadius: '6px',
              background: 'var(--color-accent-secondary)',
              color: 'var(--color-text-inverse)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BindleTermResolveDialog;
